/**
 * Code 39 barcode — a pure encoder that emits SVG.
 *
 * The receipt designer needs a barcode on the paper (GL_WEB_nojoom_live's
 * slip carries the invoice number as one, beside the header), and the two
 * places that render a design — the admin's live preview and the cashier's
 * print iframe — share one renderer in `receiptDesign.ts` that is pure by
 * contract: no DOM, no canvas, no npm barcode library that would have to be a
 * dependency of `@matix/types` and therefore of every app. So the symbology is
 * implemented here, in about forty lines, and the output is an inline `<svg>`
 * the browser rasterises at whatever resolution the printer asks for.
 *
 * **Code 39 and not Code 128** because that is what the legacy Sismatix slip
 * printed and what the tenants' hand scanners are configured for: Code 128 is
 * denser but needs the scanner to have that symbology enabled, and a barcode
 * nobody in the shop can scan is worse than no barcode. Code 39 is also
 * self-checking and needs no check digit, so what is printed under the bars is
 * exactly what scans back.
 *
 * Bar/space widths use the 3:1 ratio (the safe end of the 2.0–3.0 the spec
 * allows) — a thermal head at 203 dpi smears a narrow element, and 2:1 is
 * where that starts turning wide bars into narrow ones.
 */

/**
 * Element widths per character: nine alternating elements starting with a bar
 * (bar, space, bar, …, bar), `w` wide and `n` narrow. Exactly three of the
 * nine are wide in every row — that is what makes the symbology self-checking.
 */
const CODE39: Record<string, string> = {
  '0': 'nnnwwnwnn', '1': 'wnnwnnnnw', '2': 'nnwwnnnnw', '3': 'wnwwnnnnn',
  '4': 'nnnwwnnnw', '5': 'wnnwwnnnn', '6': 'nnwwwnnnn', '7': 'nnnwnnwnw',
  '8': 'wnnwnnwnn', '9': 'nnwwnnwnn',
  A: 'wnnnnwnnw', B: 'nnwnnwnnw', C: 'wnwnnwnnn', D: 'nnnnwwnnw',
  E: 'wnnnwwnnn', F: 'nnwnwwnnn', G: 'nnnnnwwnw', H: 'wnnnnwwnn',
  I: 'nnwnnwwnn', J: 'nnnnwwwnn', K: 'wnnnnnnww', L: 'nnwnnnnww',
  M: 'wnwnnnnwn', N: 'nnnnwnnww', O: 'wnnnwnnwn', P: 'nnwnwnnwn',
  Q: 'nnnnnnwww', R: 'wnnnnnwwn', S: 'nnwnnnwwn', T: 'nnnnwnwwn',
  U: 'wwnnnnnnw', V: 'nwwnnnnnw', W: 'wwwnnnnnn', X: 'nwnnwnnnw',
  Y: 'wwnnwnnnn', Z: 'nwwnwnnnn',
  '-': 'nwnnnnwnw', '.': 'wwnnnnwnn', ' ': 'nwwnnnwnn', $: 'nwnwnwnnn',
  '/': 'nwnwnnnwn', '+': 'nwnnnwnwn', '%': 'nnnwnwnwn',
  '*': 'nwnnwnwnn',
};

/** Wide elements are three narrow modules; the ratio the spec calls 3:1. */
const WIDE = 3;
const NARROW = 1;

/**
 * Keep only what Code 39 can express, upper-casing letters.
 *
 * Silently dropping an unencodable character is right here: the value is a
 * `{{token}}` the tenant bound in a design, and a receipt that fails to print
 * because an item name reached the barcode block helps nobody. What is dropped
 * is dropped from the human-readable line too, so the two always agree.
 */
export function sanitizeCode39(value: string): string {
  return String(value ?? '')
    .toUpperCase()
    .split('')
    .filter((c) => c !== '*' && Object.prototype.hasOwnProperty.call(CODE39, c))
    .join('');
}

export interface Code39SvgOptions {
  /** Height of the bars themselves, in px. The caption is drawn beneath. */
  heightPx?: number;
  /** Width of one narrow element, in px. */
  moduleWidthPx?: number;
  /** Print the value in readable digits under the bars. Default true. */
  showValue?: boolean;
  /** Font size of that caption, in px. */
  captionFontPx?: number;
  /** Bar colour. Thermal paper only has one, but the preview honours it. */
  color?: string;
}

/**
 * Encode `value` as a Code 39 barcode and return a standalone `<svg>` element.
 *
 * The SVG carries an explicit `width`/`height` in px AND a `viewBox`, so it
 * lays out at a predictable size inside a receipt block and still scales
 * cleanly when the print engine rasterises at 203 dpi rather than 96.
 *
 * Returns `''` for a value with nothing encodable left in it, which the
 * renderer treats as "print no barcode" rather than an empty box.
 */
export function code39Svg(value: string, opts: Code39SvgOptions = {}): string {
  const text = sanitizeCode39(value);
  if (!text) return '';

  const mod = Math.max(0.5, opts.moduleWidthPx ?? 1.4);
  const barH = Math.max(8, opts.heightPx ?? 38);
  const showValue = opts.showValue !== false;
  const captionPx = Math.max(6, opts.captionFontPx ?? 10);
  const color = opts.color || '#000';

  // Start and stop are the '*' guard, which is never part of the data.
  const chars = `*${text}*`.split('');
  const rects: string[] = [];
  let x = 0;
  chars.forEach((ch, idx) => {
    const pattern = CODE39[ch]!;
    for (let i = 0; i < pattern.length; i += 1) {
      const w = (pattern[i] === 'w' ? WIDE : NARROW) * mod;
      // Even indices are bars, odd are spaces.
      if (i % 2 === 0) rects.push(`<rect x="${round(x)}" y="0" width="${round(w)}" height="${round(barH)}"/>`);
      x += w;
    }
    // One narrow space between characters — not after the last one.
    if (idx < chars.length - 1) x += NARROW * mod;
  });

  const totalW = x;
  const capH = showValue ? captionPx + 2 : 0;
  const totalH = barH + capH;
  const caption = showValue
    ? `<text x="${round(totalW / 2)}" y="${round(totalH - 1)}" text-anchor="middle" ` +
      `font-family="monospace" font-size="${captionPx}" fill="${color}" ` +
      `letter-spacing="1">${escapeXml(text)}</text>`
    : '';

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${round(totalW)}" height="${round(totalH)}" ` +
    `viewBox="0 0 ${round(totalW)} ${round(totalH)}" shape-rendering="crispEdges" ` +
    `role="img" aria-label="${escapeXml(text)}">` +
    `<g fill="${color}">${rects.join('')}</g>${caption}</svg>`
  );
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
